import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  Button,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Search as SearchIcon,
  AutoAwesome as MagicIcon,
  QuestionAnswer as QuestionIcon,
  ContentCopy as CopyIcon,
  Lightbulb as SuggestIcon,
} from '@mui/icons-material';

interface QuestionCoverageProps {
  covered: string[];
  missing: string[];
  onAddQuestion: (question: string) => void;
}

export const QuestionCoverage: React.FC<QuestionCoverageProps> = ({
  covered,
  missing,
  onAddQuestion
}) => {
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customQuestion, setCustomQuestion] = useState('');
  const [suggestedAnswers, setSuggestedAnswers] = useState<{ [key: string]: string }>({});

  const coveragePercentage = covered.length / (covered.length + missing.length) * 100;

  const handleToggleQuestion = (question: string) => {
    setSelectedQuestions(prev =>
      prev.includes(question)
        ? prev.filter(q => q !== question)
        : [...prev, question]
    );
  };

  const handleAddSelected = () => {
    selectedQuestions.forEach(question => {
      onAddQuestion(question);
    });
    setSelectedQuestions([]);
  };

  const handleAddCustom = () => {
    if (customQuestion.trim()) {
      onAddQuestion(customQuestion);
      setCustomQuestion('');
    }
  };

  const generateSuggestedAnswer = async (question: string) => {
    // In real implementation, this would call the AI API
    // For now, return a placeholder
    setSuggestedAnswers(prev => ({
      ...prev,
      [question]: `AI-generated answer for: ${question}. This would provide a comprehensive, SEO-optimized response based on your content and competitor analysis.`
    }));
  };

  const copyQuestion = (question: string) => {
    navigator.clipboard.writeText(question);
  };

  const filteredMissing = missing.filter(q =>
    q.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getQuestionCategory = (question: string) => {
    if (question.toLowerCase().includes('how')) return 'How-to';
    if (question.toLowerCase().includes('what')) return 'What';
    if (question.toLowerCase().includes('why')) return 'Why';
    if (question.toLowerCase().includes('when')) return 'When';
    if (question.toLowerCase().includes('where')) return 'Where';
    if (question.toLowerCase().includes('who')) return 'Who';
    if (question.toLowerCase().includes('which')) return 'Comparison';
    if (question.toLowerCase().includes('cost') || question.toLowerCase().includes('price')) return 'Cost';
    return 'General';
  };

  const categorizedQuestions = filteredMissing.reduce((acc, q) => {
    const category = getQuestionCategory(q);
    if (!acc[category]) acc[category] = [];
    acc[category].push(q);
    return acc;
  }, {} as { [key: string]: string[] });

  return (
    <Box>
      {/* Coverage Overview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Customer Question Coverage (CQC)
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              Coverage: {covered.length} of {covered.length + missing.length} questions
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {coveragePercentage.toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={coveragePercentage}
            color={coveragePercentage >= 80 ? 'success' : coveragePercentage >= 60 ? 'warning' : 'error'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Paper sx={{ p: 2, bgcolor: 'success.light', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckIcon color="success" />
                <Typography variant="subtitle2">Covered Questions</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {covered.length}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Already answered in your content
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 6 }}>
            <Paper sx={{ p: 2, bgcolor: 'warning.light', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MissingIcon color="warning" />
                <Typography variant="subtitle2">Missing Questions</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {missing.length}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Opportunities to improve
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Covered Questions */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckIcon color="success" />
            <Typography>Covered Questions ({covered.length})</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {covered.map((question, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={question}
                  secondary={
                    <Chip
                      label={getQuestionCategory(question)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Copy question">
                    <IconButton size="small" onClick={() => copyQuestion(question)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Missing Questions */}
      <Accordion defaultExpanded sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MissingIcon color="warning" />
            <Typography>Missing Questions ({missing.length})</Typography>
            {selectedQuestions.length > 0 && (
              <Chip
                label={`${selectedQuestions.length} selected`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Search and Actions */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            {selectedQuestions.length > 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSelected}
                fullWidth
                sx={{ mb: 2 }}
              >
                Add {selectedQuestions.length} Selected Questions
              </Button>
            )}
          </Box>

          {/* Categorized Questions */}
          {Object.entries(categorizedQuestions).map(([category, questions]) => (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {category} ({questions.length})
              </Typography>
              <List dense>
                {questions.map((question, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedQuestions.includes(question)}
                        onChange={() => handleToggleQuestion(question)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={question}
                      secondary={
                        suggestedAnswers[question] && (
                          <Paper sx={{ p: 1, mt: 1, bgcolor: 'grey.100' }}>
                            <Typography variant="caption">
                              {suggestedAnswers[question]}
                            </Typography>
                          </Paper>
                        )
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Generate suggested answer">
                        <IconButton
                          size="small"
                          onClick={() => generateSuggestedAnswer(question)}
                          color="primary"
                        >
                          <SuggestIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy question">
                        <IconButton
                          size="small"
                          onClick={() => copyQuestion(question)}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Custom Question */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Custom Question
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter a custom question to address..."
            value={customQuestion}
            onChange={(e) => setCustomQuestion(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCustom()}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCustom}
            disabled={!customQuestion.trim()}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* AI Suggestions */}
      <Paper sx={{ p: 2, mt: 2, bgcolor: 'primary.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <MagicIcon />
          <Typography variant="subtitle2">
            AI Recommendation
          </Typography>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          Based on your content analysis, focusing on these top 3 questions will have the biggest impact:
        </Typography>
        
        <List dense>
          {missing.slice(0, 3).map((question, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Chip label={index + 1} size="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary={question} />
              <Button
                size="small"
                variant="outlined"
                onClick={() => onAddQuestion(question)}
              >
                Quick Add
              </Button>
            </ListItem>
          ))}
        </List>
        
        <Button
          variant="contained"
          fullWidth
          startIcon={<MagicIcon />}
          onClick={() => missing.slice(0, 3).forEach(q => onAddQuestion(q))}
          sx={{
            mt: 2,
            background: 'linear-gradient(45deg, #9c27b0 30%, #673ab7 90%)',
          }}
        >
          Add All Top 3 Questions
        </Button>
      </Paper>
    </Box>
  );
};